import { Icon, Skeleton, SxProps, Tooltip, Typography } from '@mui/material';
import {
  AssignedProduct,
  Company,
  DefaultPayer,
  Payer,
  PayerType,
  PaymentFrequency,
} from '@schooly/api';
import {
  CompanyIcon,
  GridCell,
  GridContainer,
  GridRowCell,
  GridRowName,
  LockIcon,
  Price,
  PRICE_SUBTEXT_CLASS_NAME,
  ProfileIcon,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { getUserFullName } from '@schooly/utils/user-helpers';
import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { Table, TableCell } from '../../../../components/uikit-components/Table/Table';
import { getCurrencySymbol } from '../../../School/SchoolProducts/helpers';
import { AssignedProductPayer } from './AssignedProductPayer';

interface AssignedProductsTableProps {
  products: AssignedProduct[];
  payer?: DefaultPayer;
  company?: Company;
  frequencies?: PaymentFrequency[];
  headerTitle: string;
  sx?: SxProps;
  schoolId: string;
  withPayer?: boolean;
  isActive?: boolean;
}

export const AssignedProductsTable: FC<AssignedProductsTableProps> = ({
  products,
  frequencies,
  headerTitle,
  sx,
  schoolId,
  withPayer,
  isActive = true,
}) => {
  const { formatMessage } = useIntl();

  const renderPayer = useCallback(
    (payer: Payer) => {
      const isCompanyPayer = payer.type === PayerType.Company;
      const id = isCompanyPayer ? payer.data.id : payer.data.relation_id;
      const name = isCompanyPayer ? payer.data.name : getUserFullName(payer.data);
      const contactName = isCompanyPayer ? payer.data.contact_name : undefined;
      const inviteStatus = isCompanyPayer ? undefined : payer.data.invite_status;
      const icon = isCompanyPayer ? <CompanyIcon /> : <ProfileIcon />;

      if (!id) return null;

      return (
        <AssignedProductPayer
          relationId={id}
          name={name}
          telephone={payer.data.telephone}
          email={payer.data.email}
          contactName={contactName}
          inviteStatus={inviteStatus}
          icon={icon}
          schoolId={schoolId}
        />
      );
    },
    [schoolId],
  );

  const columns: TableCell[] = useMemo(
    () => [
      {
        id: 'Name',
        renderContent: () => (
          <GridCell py={1}>
            <Typography
              variant="h4"
              color="text.secondary"
              sx={{ '&.MuiTypography-root': { marginBottom: 0 } }}
            >
              {headerTitle}
            </Typography>
          </GridCell>
        ),
      },
      ...(withPayer
        ? [
            {
              id: 'Payer',
              renderContent: () => (
                <GridCell py={1}>{formatMessage({ id: 'profile-Payer' })}</GridCell>
              ),
            },
          ]
        : []),
      {
        id: 'Frequency',
        renderContent: () => (
          <GridCell py={1}>{formatMessage({ id: 'profile-Frequency' })}</GridCell>
        ),
      },
      {
        id: 'Price',
        renderContent: () => <GridCell py={1}>{formatMessage({ id: 'profile-Price' })}</GridCell>,
      },
      {
        id: 'Discount',
        renderContent: () => <GridCell py={1}>{formatMessage({ id: 'profile-Dis' })}</GridCell>,
      },
      {
        id: 'Amount',
        renderContent: () => <GridCell py={1}>{formatMessage({ id: 'profile-Amount' })}</GridCell>,
      },
    ],
    [formatMessage, headerTitle, withPayer],
  );

  const rows = useMemo(() => {
    if (!products) {
      return [];
    }
    return products.map((product) => {
      const frequency = frequencies?.find((f) => f.id === product.variant.frequency_id);
      const currencySymbol = getCurrencySymbol(product.variant.currency);
      const name = product.variant.type_name
        ? `${product.name} - ${product.variant.type_name}`
        : product.name;

      const cellArray: TableCell[] = [
        {
          id: 'Name',
          renderContent: () => (
            <GridCell sx={{ maxWidth: 300 }} py={1}>
              <GridRowName sx={{ display: 'flex' }} gap={1}>
                {product.obligatory && <AssignedProductObligatoryIcon />}
                <TypographyWithOverflowHint
                  variant="h3"
                  component={Link}
                  to={`/settings/products/${product.id}`}
                  sx={{ '&:hover': { textDecoration: 'underline' } }}
                >
                  {name}
                </TypographyWithOverflowHint>
              </GridRowName>
            </GridCell>
          ),
        },
        ...(withPayer
          ? [
              {
                id: 'Payer',
                renderContent: () => (
                  <GridCell sx={{ width: 45 }} py={1}>
                    <GridRowCell>{renderPayer(product.payer)}</GridRowCell>
                  </GridCell>
                ),
              },
            ]
          : []),
        {
          id: 'Frequency',
          renderContent: () => (
            <GridCell sx={{ width: 100 }} py={1}>
              <GridRowCell>
                {frequency ? (
                  <Typography>{formatMessage({ id: `frequencies-${frequency.type}` })}</Typography>
                ) : (
                  <Skeleton />
                )}
              </GridRowCell>
            </GridCell>
          ),
        },
        {
          id: 'Price',
          renderContent: () => (
            <GridCell sx={{ width: 120 }} py={1}>
              <GridRowCell>
                <Price
                  variant="body1"
                  price={product.variant.price}
                  currency={currencySymbol}
                  isOldPrice={!!product.discount_percent}
                />
              </GridRowCell>
            </GridCell>
          ),
        },
        {
          id: 'Discount',
          renderContent: () => (
            <GridCell sx={{ width: 60 }} py={1}>
              <GridRowCell>
                <Typography>
                  {product.discount_percent ? `${product.discount_percent}%` : '-'}
                </Typography>
              </GridRowCell>
            </GridCell>
          ),
        },
        {
          id: 'Amount',
          renderContent: () => (
            <GridCell sx={{ width: 120 }} py={1}>
              <GridRowCell>
                <Price
                  variant="body1"
                  price={product.discounted_price ?? product.variant.price}
                  currency={currencySymbol}
                />
              </GridRowCell>
            </GridCell>
          ),
        },
      ];

      return {
        id: product.id,
        cells: cellArray,
      };
    });
  }, [formatMessage, frequencies, products, renderPayer, withPayer]);

  return (
    <GridContainer
      sx={{
        '& .MuiTableBody-root .MuiTableRow-root': {
          ':hover': {
            [` .${PRICE_SUBTEXT_CLASS_NAME}`]: {
              color: 'common.grey2',
            },
            ' .MuiAvatar-root': {
              bgcolor: 'background.paper',
            },
          },
        },
        ...(!isActive && {
          ' .MuiTableRow-root:not(:hover)': {
            ' .MuiTypography-root': {
              color: 'text.secondary',
            },
            ' .MuiIcon-root': {
              color: 'common.grey',
            },
            ' .MuiAvatar-root': {
              ' .MuiIcon-root': {
                color: 'common.grey2',
              },
            },
          },
          ' .MuiTableBody-root .MuiTableRow-root:hover': {
            ' .MuiTypography-root': {
              color: 'text.primary',
            },
            ' .MuiIcon-root': {
              color: 'common.grey2',
            },
            [` .${PRICE_SUBTEXT_CLASS_NAME}`]: {
              color: 'text.secondary',
            },
          },
        }),
        ...sx,
      }}
    >
      <Table columns={columns} rows={rows} withDefaultHover />
    </GridContainer>
  );
};

const AssignedProductObligatoryIcon = () => {
  const { formatMessage } = useIntl();

  return (
    <Tooltip
      arrow={false}
      title={
        <Typography variant="caption">
          {formatMessage({ id: 'profile-RequiredProduct' })}
        </Typography>
      }
      componentsProps={{
        tooltip: {
          sx: (theme) => ({
            padding: theme.spacing(0, 0.5),
            borderRadius: theme.spacing(0.5),
            marginTop: theme.spacing(1),
          }),
        },
      }}
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -14],
            },
          },
        ],
      }}
    >
      <Icon>
        <LockIcon />
      </Icon>
    </Tooltip>
  );
};
